/**
 * ABM service pages — approved copy from abmparking.com/services, reproduced verbatim.
 *
 * The closing "Contact ABM Today!" links open ABM's own sales-inquiry form on abmparking.com in a new
 * tab, exactly where the source site sends them — this site has no forms.
 */
import type { AbmDetailPage, AbmIndexIntro } from './abm-content.types';

export const ABM_SERVICES_INTRO: AbmIndexIntro = {
  title: 'Our Services',
  metaTitle: 'Services',
  text: 'End-to-end parking solutions, powered by service and technology.',
  heroImage: 'v2-contact-header.webp',
};

const CONTACT_CTA = {
  label: 'Contact ABM Today!',
  href: 'https://abmparking.com/contact/?form=sales-inquiries',
  external: true,
};

export const ABM_SERVICES: AbmDetailPage[] = [
  {
    slug: 'call-center',
    title: 'Call Center',
    summary:
      'In the new era of parking management, ABM balances advanced technology with human support to enhance automated systems. Our expertise helps lower labor costs, reduce theft, and improve traffic flow and operations. Our state-of-the-art 24/7/365 call center oversees all parking operations.',
    heroImage: 'v2-service-call-center.webp',
    cardImage: 'v2-service-call-center-28.webp',
    sections: [
      {
        heading: 'ABM 24/7 call center',
        blocks: [
          {
            kind: 'text',
            text: 'Our call center line is available for any ABM parking customer nationwide.',
          },
          {
            kind: 'list',
            items: [
              'Fully trained customer service staff',
              'Operates 24/7/365',
              'State-of-the-art reporting software',
            ],
          },
          { kind: 'text', text: 'The ABM call center is staffed and operated by ABM employees.' },
          {
            kind: 'list',
            items: [
              'Customized “scripts” by request',
              'Audio/video monitoring',
              'Internet Protocol (IP)-based feed for audio/video',
              'Dial-up for audio only',
            ],
          },
        ],
        image: 'abm-v2-call-center-001.webp',
      },
    ],
    closing: { heading: 'Need parking solutions? ABM makes it simple.', cta: CONTACT_CTA },
  },
  {
    slug: 'parkabm-platform',
    title: 'ParkABM Platform',
    summary:
      "ParkABM is the industry's leading enterprise parking software platform, designed to simplify the parking business and ensure compliance with regulatory requirements, including Sarbanes-Oxley (SOX). Discover how ParkABM can transform your parking operations with its comprehensive, customizable solutions.",
    heroImage: 'v2-service-parkabm-platform.webp',
    cardImage: 'v2-service-parkabm-platform-30.webp',
    sections: [
      {
        heading: 'End-to-end technology solution',
        blocks: [
          {
            kind: 'list',
            items: [
              'Integrated Platform: ParkABM combines previously separate solutions into one powerful platform, addressing all your business requirements.',
              'Revenue Management: Manage and report revenues with the industry’s most widely utilized revenue management system.',
              'B2C Sales Website: Seamlessly interact with customers through a fully integrated sales website.',
              'Monthly Billing System: Efficiently manage recurring parkers with our streamlined billing system.',
              'Daily Reporting: Access detailed, real-time data to make strategic decisions.',
            ],
          },
        ],
        image: 'v2-parkabm-end-to-end.webp',
      },
      {
        heading: 'Maximize efficiency and cash flow',
        blocks: [
          {
            kind: 'text',
            text: 'Automate complex tenant lease provisions, including must-takes, discounts, and scheduled rate increases. Ensure accurate billing, reconciliation, and reporting of revenue line items. Provide direct individual and corporate account management with flexible access options. Benefit from real-time integrations with cloud-based garage and building access systems.',
          },
          {
            kind: 'list',
            items: [
              'Back Office Administration: Handle account refunds, credits, adjustments, and other functions. Tenant administrators can manage accounts, view history, and perform key functions online.',
              'Daily Reporting and Bank Reconciliations: Consolidate daily data for accuracy and accountability. Perform daily reconciliations for bank deposits and credit card receipts.',
            ],
          },
        ],
        image: 'v2-parkabm-maximize-efficiency.webp',
      },
      {
        heading: 'Websites and marketing tools',
        blocks: [
          {
            kind: 'list',
            items: [
              'Custom Websites: Develop and host websites for parking operators and venues.',
              'Landing Pages: Customizable pages with on-page purchase capabilities.',
              'Targeted Marketing: Analyze customer data for targeted campaigns.',
              'Online Presence: Enhance visibility through SEO and map placement',
            ],
          },
          { kind: 'subheading', text: 'Hourly Reservations' },
          {
            kind: 'text',
            text: 'Customers can search for and purchase hourly and monthly parking, with all transactions flowing through a single channel. Seamlessly enter and exit garages using QR code-based Boarding Pass.',
          },
          {
            kind: 'text',
            text: 'Maximize efficiency and cash flow with ParkABM’s fully connected platform. Enhance the customer experience and streamline your parking operation.',
          },
        ],
        image: 'v2-parkabm-website-2.webp',
      },
    ],
    closing: {
      heading: 'Ready to optimize your parking operations? Let’s connect.',
      cta: CONTACT_CTA,
    },
  },
  {
    slug: 'parking-enforcement',
    title: 'Parking Enforcement & Meter Collections',
    summary:
      'ABM brings the strength of a national organization with the flexibility and responsiveness of a local partner. With decades of experience and a nationwide presence, ABM Parking is a recognized leader in meter collection and street enforcement services. We partner with municipalities, transit authorities, and property managers across the country to deliver efficient, transparent, and community-focused parking enforcement solutions.',
    heroImage: 'v2-service-parking-enforcement.webp',
    cardImage: 'v2-service-parking-enforcement-32.webp',
    sections: [
      {
        heading: 'Street enforcement services',
        blocks: [
          {
            kind: 'text',
            text: 'ABM understands the importance of maintaining order, compliance, and fairness in public and private parking spaces. Our street enforcement teams are trained professionals who:',
          },
          {
            kind: 'list',
            items: [
              'Monitor on-street and off-street parking zones',
              'Enforce local parking regulations with accuracy and consistency',
              'Use mobile enforcement technology to streamline citations and real-time monitoring',
              'Provide courteous, customer-focused interactions to support community relations',
            ],
          },
          {
            kind: 'text',
            text: 'Our enforcement programs are tailored to each jurisdiction’s needs, helping cities reduce unauthorized parking, improve turnover, and enhance accessibility.',
          },
        ],
        image: 'v2-enforcement-street-enforcement.webp',
      },
      {
        heading: 'Comprehensive meter collection services',
        blocks: [
          {
            kind: 'text',
            text: 'ABM brings deep operational expertise to parking meter collections, ensuring secure, accountable, and efficient revenue management. Our services include:',
          },
          {
            kind: 'list',
            items: [
              'Secure Collection Protocols: We follow strict procedures to safeguard parking revenues during transport and processing.',
              'Accurate Accounting & Reporting: Detailed reconciliation and transparent reporting ensure fiscal accountability and support for municipal audits and budgeting.',
              'Meter Maintenance & Repair: ABM ensures parking meters are regularly serviced, calibrated, and operational, helping maximize revenue and maintain user satisfaction.',
            ],
          },
        ],
        image: 'v2-enforcement-meter-collection.webp',
      },
      {
        heading: 'Technology-driven enforcement & collection',
        blocks: [
          { kind: 'text', text: 'We leverage advanced tools to enhance service delivery:' },
          {
            kind: 'list',
            items: [
              'License Plate Recognition (LPR) for quick vehicle identification',
              'Mobile citation platforms for faster, error-free enforcement',
              'Real-time data and analytics for performance tracking and decision-making',
              'Digital payment integration to support modern parking solutions',
            ],
          },
          {
            kind: 'text',
            text: 'Partner with ABM Parking to bring professionalism, innovation, and accountability to your enforcement and collection operations—from curb to meter.',
          },
        ],
        image: 'v2-enforcement-technology-driven.webp',
      },
    ],
    closing: { heading: 'Your trusted parking partner — just a click away.', cta: CONTACT_CTA },
  },
  {
    slug: 'shuttle-services',
    title: 'Shuttle Services',
    summary:
      'ABM operates a fleet of over 1,000 shuttles, transporting more than 47 million passengers annually across the United States. Serving a wide array of sectors—including airports, corporate campuses, hospitals, universities, and event venues—ABM delivers dependable and efficient transportation solutions.',
    heroImage: 'v2-service-shuttle-services.webp',
    cardImage: 'v2-service-shuttle-services-34.webp',
    sections: [
      {
        heading: 'Reliable shuttle services backed by professionalism',
        blocks: [
          {
            kind: 'text',
            text: 'With a strong focus on safety, punctuality, and vehicle upkeep, ABM maintains rigorous maintenance schedules to ensure every ride is reliable, comfortable, and on time.',
          },
          {
            kind: 'text',
            text: 'ABM’s team of shuttle service professionals undergoes rigorous training to ensure the highest standards of professionalism and courtesy. Drivers are vetted thoroughly and trained to assist passengers effectively, enhancing the overall service quality.',
          },
          {
            kind: 'text',
            text: 'Whether it’s moving employees across a corporate campus or ensuring seamless transit at a major event, ABM’s shuttle services are designed for high performance at scale.',
          },
        ],
        image: 'v2-shuttle-reliable.webp',
      },
      {
        heading: 'Key offerings',
        blocks: [
          {
            kind: 'list',
            items: [
              'Fleet Management: ABM ensures company vehicles are consistently maintained and operational, minimizing downtime and keeping business operations running smoothly.',
              'Eco-Friendly Options: With a strong commitment to sustainability, ABM offers electric and hybrid vehicles, supporting companies in reducing their carbon footprint.',
              'GPS tracking and real-time route optimization to ensure efficient travel',
              'Mobile apps for scheduling, tracking, and employee communication',
              'Regular vehicle inspections and driver training programs',
              'Strict adherence to local regulations and industry standards',
            ],
          },
          {
            kind: 'text',
            text: 'ABM helps businesses streamline transportation logistics, enhance customer experience, and contribute to a cleaner, greener future.',
          },
        ],
        image: 'v2-shuttle-key-offerings.webp',
      },
    ],
    closing: {
      heading: 'Let’s streamline your parking operations with ABM expertise.',
      cta: CONTACT_CTA,
    },
  },
  {
    slug: 'valet-parking',
    title: 'Valet Parking',
    summary:
      'ABM delivers premier valet and hospitality services for commercial, office, residential, hotels, and mixed-use properties, backed by decades of experience and a national footprint. We proudly serve prestigious clients across the country, including luxury office towers, corporate campuses, healthcare institutions, and iconic destination properties.',
    heroImage: 'abm-v2-valet-parking-001.webp',
    cardImage: 'abm-v2-valet-parking-001.webp',
    sections: [
      {
        heading: 'The ABM difference: elevating every arrival',
        blocks: [
          {
            kind: 'text',
            text: 'Our valet services are designed to enhance the arrival experience through a perfect blend of professionalism, hospitality, and innovation. With a focus on customer satisfaction and operational efficiency, ABM sets the standard for valet excellence coast to coast.',
          },
          {
            kind: 'list',
            items: [
              'Extensive valet and hospitality expertise across diverse environments',
              'A trusted partner for high-profile clients nationwide',
              'Highly trained staff delivering prompt, courteous, white-glove service',
              'Strategic operations that ensure safety, speed, and efficiency',
            ],
          },
        ],
        image: 'abm-la-parking-service-7039.webp',
      },
      {
        heading: 'Embracing technology for a better experience',
        blocks: [
          {
            kind: 'text',
            text: 'ABM integrates cutting-edge technology to streamline the valet process and enhance user satisfaction, including:',
          },
          {
            kind: 'list',
            items: [
              'Digital payment systems for fast, contactless transactions',
              'Real-time vehicle tracking and retrieval notifications',
              'Mobile-friendly platforms for scheduling, tipping, and customer feedback',
            ],
          },
          {
            kind: 'text',
            text: 'Our solutions not only maximize revenue and space utilization, but also deliver a modern, elevated valet experience that reflects the standards of today’s top-tier properties.',
          },
        ],
        image: 'v2-valet-embracing-technology.webp',
      },
    ],
    closing: { heading: 'Smarter parking starts with ABM.', cta: CONTACT_CTA },
  },
  {
    slug: 'vehicle-services',
    title: 'Vehicle Services',
    summary:
      'ABM supports rental car providers with professional vehicle retrieval services that streamline operations and elevate the customer experience. From airport lots to off-site locations, our trained teams handle every step of the process with care, speed, and precision — so your customers get on the road faster and happier.',
    heroImage: 'v2-service-vehicle-services.webp',
    cardImage: 'v2-service-vehicle-services-36.webp',
    sections: [
      {
        heading: 'Delivering seamless rental car experiences',
        blocks: [
          {
            kind: 'text',
            text: 'Our teams locate, retrieve, and stage rental vehicles quickly and efficiently, reducing customer wait times and improving operational flow.',
          },
          { kind: 'subheading', text: 'Clean, Ready-to-Go Vehicles' },
          {
            kind: 'list',
            items: [
              'Before handoff, we inspect and clean each vehicle to meet your brand standards — spotless interiors, gleaming exteriors, and a ready-to-drive feel.',
              'Every minute counts in the rental car business — and ABM helps you make each one count. With reliable, responsive vehicle services, we support a better experience for your customers and smoother operations for your team.',
              'Whether managing airport fleets, overflow lots, or staging areas, ABM’s staff ensure vehicles are always in the right place at the right time.',
            ],
          },
          {
            kind: 'text',
            text: 'Partner with ABM and keep your fleet — and your customers — moving.',
          },
        ],
        image: 'v2-vehicle-services-delivering.webp',
      },
    ],
    closing: { heading: 'Transform your parking experience with ABM.', cta: CONTACT_CTA },
  },
];
